/** @jsx jsx */
import React, { useMemo } from 'react';
import { css, jsx } from '@compiled/react';
import type { TimeTrackingOptions } from '@atlassian/jira-time-tracking-formatter';
import { ORIGINAL_ESTIMATE, STORY_POINTS } from '../../../../../model/constants';
import type { EstimationType } from '../../../../../model/types';
import type { Assignee } from '../../types';
import SummaryTableBody from './summary-table-body';
import SummaryTableFooter from './summary-table-footer';
import SummaryTableHeader from './summary-table-header';

type Props = {
	assignees: Assignee[];
	unassigned?: Assignee;
	estimationType: EstimationType;
	isEstimationEnabled: boolean;
	isRemainingTimeTrackingEnabled: boolean;
	timeTrackingOptions: TimeTrackingOptions;
	estimationFieldName: string;
	includeSubtasks: boolean;
};

const SummaryTable = ({
	assignees,
	unassigned,
	isEstimationEnabled,
	isRemainingTimeTrackingEnabled,
	estimationType,
	timeTrackingOptions,
	estimationFieldName,
	includeSubtasks,
}: Props) => {
	const total = useMemo(
		() =>
			assignees.reduce(
				(prev, assignee) => ({
					storyPoints: prev.storyPoints + assignee.storyPoints,
					remainingTimeEstimateInSeconds:
						prev.remainingTimeEstimateInSeconds + assignee.remainingTimeEstimateInSeconds,
					originalTimeEstimateInSeconds:
						prev.originalTimeEstimateInSeconds + assignee.originalTimeEstimateInSeconds,
					issueCount: prev.issueCount + assignee.issueCount,
					subtaskCount: (prev.subtaskCount ?? 0) + (assignee.subtaskCount ?? 0),
				}),
				unassigned ?? {
					storyPoints: 0,
					remainingTimeEstimateInSeconds: 0,
					originalTimeEstimateInSeconds: 0,
					issueCount: 0,
					subtaskCount: 0,
				},
			),
		[assignees, unassigned],
	);

	const isStoryPointsEnabled = estimationType === STORY_POINTS && isEstimationEnabled;
	const isOriginalEstimationEnabled = estimationType === ORIGINAL_ESTIMATE && isEstimationEnabled;
	const isRemainingTimeEstimationEnabled = isRemainingTimeTrackingEnabled && isEstimationEnabled;

	return (
		<table css={styles}>
			<SummaryTableHeader
				isStoryPointsEnabled={isStoryPointsEnabled}
				isOriginalEstimationEnabled={isOriginalEstimationEnabled}
				isRemainingTimeEstimationEnabled={isRemainingTimeEstimationEnabled}
				estimationFieldName={estimationFieldName}
				includeSubtasks={includeSubtasks}
			/>
			<SummaryTableBody
				isStoryPointsEnabled={isStoryPointsEnabled}
				isOriginalEstimationEnabled={isOriginalEstimationEnabled}
				isRemainingTimeEstimationEnabled={isRemainingTimeEstimationEnabled}
				assignees={assignees}
				unassigned={unassigned}
				timeTrackingOptions={timeTrackingOptions}
				includeSubtasks={includeSubtasks}
				estimationType={estimationType}
			/>
			<SummaryTableFooter
				total={total}
				isStoryPointsEnabled={isStoryPointsEnabled}
				isOriginalEstimationEnabled={isOriginalEstimationEnabled}
				isRemainingTimeEstimationEnabled={isRemainingTimeEstimationEnabled}
				timeTrackingOptions={timeTrackingOptions}
				includeSubtasks={includeSubtasks}
			/>
		</table>
	);
};

export default SummaryTable;
const styles = css({ borderCollapse: 'separate', borderSpacing: 0 });
